import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { api, ApiError, getRemoteSession } from './api';
import { useAuthStore } from './auth';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { PendingPage } from './pages/PendingPage';
import { DashboardPage } from './pages/DashboardPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { VenuesPage } from './pages/VenuesPage';
import { VenueDetailPage } from './pages/VenueDetailPage';
import { TeamsPage } from './pages/TeamsPage';
import { TeamDetailPage } from './pages/TeamDetailPage';
import { GamesPage } from './pages/GamesPage';
import { GameControlPage } from './pages/GameControlPage';
import { GameRulesPage } from './pages/GameRulesPage';
import { DisplaysPage } from './pages/DisplaysPage';
import { MembersPage } from './pages/MembersPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignDetailPage } from './pages/CampaignDetailPage';
import { TournamentsPage } from './pages/TournamentsPage';
import { TournamentDetailPage } from './pages/TournamentDetailPage';
import { StatsTrackerPage } from './pages/StatsTrackerPage';
import { JoinPage } from './pages/JoinPage';
import { LayoutsPage } from './pages/LayoutsPage';
import { LayoutBuilderPage } from './pages/LayoutBuilderPage';

export default function App() {
  return (
    <BrowserRouter>
      <MeLoader />
      <Routes>
        <Route path="/login" element={<RedirectIfAuthed><LoginPage /></RedirectIfAuthed>} />
        <Route path="/signup" element={<RedirectIfAuthed><SignupPage /></RedirectIfAuthed>} />
        <Route path="/pending" element={<Authed><PendingPage /></Authed>} />
        <Route path="/superadmin" element={<Authed requirePlatformAdmin><SuperAdminPage /></Authed>} />
        <Route path="/" element={<Authed requireActive><HomeForRole /></Authed>} />
        <Route path="/venues" element={<Authed requireActive><VenuesPage /></Authed>} />
        <Route path="/venues/:id" element={<Authed requireActive><VenueDetailPage /></Authed>} />
        <Route path="/teams" element={<Authed requireActive><TeamsPage /></Authed>} />
        <Route path="/teams/:id" element={<Authed requireActive><TeamDetailPage /></Authed>} />
        <Route path="/games" element={<Authed requireActive><GamesPage /></Authed>} />
        <Route path="/games/:id" element={<Authed requireActive><GameControlPage /></Authed>} />
        <Route path="/games/:id/stats" element={<Authed requireActive><StatsTrackerPage /></Authed>} />
        <Route path="/settings/game-rules" element={<Authed requireActive><GameRulesPage /></Authed>} />
        <Route path="/displays" element={<Authed requireActive><DisplaysPage /></Authed>} />
        <Route path="/settings/members" element={<Authed requireActive><MembersPage /></Authed>} />
        <Route path="/campaigns" element={<Authed requireActive><CampaignsPage /></Authed>} />
        <Route path="/campaigns/:id" element={<Authed requireActive><CampaignDetailPage /></Authed>} />
        <Route path="/tournaments" element={<Authed requireActive><TournamentsPage /></Authed>} />
        <Route path="/tournaments/:id" element={<Authed requireActive><TournamentDetailPage /></Authed>} />
        <Route path="/layouts" element={<Authed requireActive><LayoutsPage /></Authed>} />
        <Route path="/layouts/:id" element={<Authed requireActive><LayoutBuilderPage /></Authed>} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Loads /me whenever we have an access token but no profile yet.
function MeLoader() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const clear = useAuthStore((s) => s.clear);

  useEffect(() => {
    if (!accessToken || me) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.me();
        if (!cancelled) setMe(result);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          if (!cancelled) clear();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, me, setMe, clear]);

  return null;
}

// Stats keepers have no dashboard — send them straight to Games.
function HomeForRole() {
  const me = useAuthStore((s) => s.me);
  if (me?.user.role === 'STATS_KEEPER') {
    return <Navigate to="/games" replace />;
  }
  return <DashboardPage />;
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  if (accessToken) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Authed({
  children,
  requireActive,
  requirePlatformAdmin,
}: {
  children: React.ReactNode;
  requireActive?: boolean;
  requirePlatformAdmin?: boolean;
}) {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const me = useAuthStore((s) => s.me);

  // A remote pairing session (no-login device) is allowed straight through
  // to its scoped console; the backend guard enforces game + role scope.
  if (getRemoteSession()) {
    return <>{children}</>;
  }

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!me) {
    // /me still loading
    return (
      <div className="flex h-screen items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (requirePlatformAdmin && !me.user.isPlatformAdmin) {
    return <Navigate to="/" replace />;
  }
  if (requireActive && me.org.status !== 'ACTIVE' && !me.user.isPlatformAdmin) {
    return <Navigate to="/pending" replace />;
  }
  return <>{children}</>;
}
